import React, { ReactNode, useCallback, useEffect } from 'react';
import { vars } from '../../theme';
import {
    Container,
    Header,
    Title,
    StyledCloseIcon,
    Footer,
    StepCounter,
    NextButton,
    Description,
    StyledGIF,
    StyledArrowIcon,
    TourActionsContainer,
    InnerContainer
} from './HBTourTooltip.styles';
import { Lightbulb24 } from '@honeybook/icons';

export interface Props {
    /** react-joyride callback data containing tooltipProps, skipProps, primaryProps, backProps, and step metadata. */
    joyrideProps: any;
    /** Heading text displayed in the tooltip header. */
    title: string;
    /** Body content displayed below the title; accepts a string or JSX. */
    description: string | ReactNode;
    /** Current step number displayed in the "X of Y" counter; sourced from step data via joyrideProps. */
    stepIndex?: number;
    /** Total number of steps displayed in the "X of Y" counter; sourced from step data via joyrideProps. */
    stepCounter?: number;
    /** Horizontal pixel offset applied to the tooltip container position. */
    horizontalOffset?: number;
    /** Vertical pixel offset applied to the tooltip container position. */
    verticalOffset?: number;
    /** URL of an image or GIF displayed above the header in the tooltip. */
    imgUrl?: string;
    /** Controls visibility of the close and back navigation buttons in the tooltip header. */
    showCloseButton?: boolean;
    /** Text for the primary action button in the tooltip footer. */
    CtaButtonText?: string;
    /** Additional content rendered before the tooltip body, outside the inner container. */
    extraContent?: ReactNode | ReactNode[];
}

const HBTourTooltip = ({
    title,
    description,
    joyrideProps,
    horizontalOffset,
    verticalOffset,
    imgUrl,
    showCloseButton = true,
    CtaButtonText = 'Next',
    extraContent
}: Props) => {
    const {
        tooltipProps,
        skipProps: { onClick: onCloseClick },
        primaryProps: { onClick: onNextClick },
        backProps: { onClick: onBackClick },
        step: { stepIndex, stepCounter }
    } = joyrideProps;

    const handleUserKeyPress = useCallback(
        event => {
            const { key } = event;
            if (key === 'Enter') {
                onNextClick(event);
            }
        },
        [onNextClick]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);
        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    return (
        <Container
            {...tooltipProps}
            verticalOffset={verticalOffset}
            horizontalOffset={horizontalOffset}
            id="tooltip_container"
        >
            {extraContent}
            {showCloseButton && (
                <TourActionsContainer>
                    {stepIndex !== 1 && (
                        <StyledArrowIcon
                            onClick={onBackClick}
                            color={vars.colors.text.tertiary}
                            data-testid="tour-back--button"
                        />
                    )}
                    <StyledCloseIcon
                        onClick={onCloseClick}
                        color={vars.colors.text.tertiary}
                        data-testid="tour-close--button"
                    />
                </TourActionsContainer>
            )}
            <InnerContainer>
                {imgUrl && <StyledGIF src={imgUrl} />}
                <Header>
                    <Lightbulb24 useDefaultFill />
                    <Title type="emphasis16">{title}</Title>
                </Header>
                <Description type="body14">{description}</Description>

                <Footer>
                    <NextButton
                        id="tour_next_button"
                        testId="tour-next--button"
                        onClick={onNextClick}
                        customTheme={{
                            background: vars.colors.base.accent,
                            hoverBackgroundColor: vars.colors.base.accentHover
                        }}
                    >
                        {CtaButtonText}
                    </NextButton>
                    {stepIndex && (
                        <StepCounter>
                            {stepIndex} of {stepCounter}
                        </StepCounter>
                    )}
                </Footer>
            </InnerContainer>
        </Container>
    );
};

export default HBTourTooltip;
